import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import {
  getSuperAdminDashboard,
  getSuperAdminEmployees,
  assignEmployeeToSuperAdmin,
  removeEmployeeFromSuperAdmin,
  getEmployeeAttendanceHistory,
} from "../../Api/superAdminApi";
import {
  Clock,
  RefreshCw,
  UserPlus,
  MapPin,
  Camera,
  CheckCircle,
  AlertCircle,
  X,
  History,
  ExternalLink,
  Eye,
  Search,
  GripVertical,
  Pin,
  Calendar,
} from "lucide-react";

/**
 * Generates initials avatar text from employee name (e.g. Ramesh Kumar -> RK)
 */
const getInitials = (name) => {
  if (!name) return "E";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Safely converts any raw date string (ISO, GMT, or YYYY-MM-DD) into clean DD Month YYYY format (e.g. 05 September 2026)
 */
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  try {
    const str = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split("-");
      const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
      return dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    }
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });
    }
  } catch (e) {}
  return String(dateStr);
};

/**
 * Safely converts any raw date string into clean short DD Mon YYYY format (e.g. 05 Sep 2026)
 */
const formatShortDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const str = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split("-");
      const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
      return dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });
    }
  } catch (e) {}
  return String(dateStr);
};

/**
 * Safely converts any raw time string / timestamp into clean 12-hour format (e.g. 09:32 AM)
 */
const formatCleanTime = (timeStr) => {
  if (!timeStr) return "--";
  const str = String(timeStr).trim();
  if (/^\d{1,2}:\d{2}(\s*(AM|PM|am|pm))?$/.test(str)) {
    return str;
  }
  try {
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });
    }
  } catch (e) {}
  return str;
};

/**
 * Derives activity label and badge styling from attendance check-in/out
 */
const getActivityInfo = (attendance) => {
  const hasCheckIn = Boolean(attendance?.checkIn?.time);
  const hasCheckOut = Boolean(attendance?.checkOut?.time);

  if (!hasCheckIn) {
    return {
      label: "Not Checked In",
      badgeClass: "bg-slate-100 text-slate-600 border-slate-200",
      dotClass: "bg-slate-400",
    };
  } else if (!hasCheckOut) {
    return {
      label: "Currently Working",
      badgeClass: "bg-amber-50 text-amber-800 border-amber-200/80",
      dotClass: "bg-amber-500 animate-pulse",
    };
  } else {
    return {
      label: "Checked Out",
      badgeClass: "bg-emerald-50 text-emerald-800 border-emerald-200/80",
      dotClass: "bg-emerald-500",
    };
  }
};

const SuperAdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [todayDate, setTodayDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Main Dashboard Team Filter Search
  const [mainSearchTerm, setMainSearchTerm] = useState("");

  // Modals and Lightbox state
  const [selectedItem, setSelectedItem] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [historyFromDate, setHistoryFromDate] = useState("");
  const [historyToDate, setHistoryToDate] = useState("");

  // Location Address Reverse Geocoding state
  const [checkInLocationName, setCheckInLocationName] = useState("");
  const [checkOutLocationName, setCheckOutLocationName] = useState("");
  const [loadingLocations, setLoadingLocations] = useState(false);

  useEffect(() => {
    if (!selectedItem) {
      setCheckInLocationName("");
      setCheckOutLocationName("");
      return;
    }

    const fetchAddress = async (lat, lng) => {
      if (!lat || !lng) return "";
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
        );
        const data = await res.json();
        if (data && data.display_name) {
          return data.display_name;
        }
        if (data && data.address) {
          const parts = [
            data.address.road || data.address.suburb || data.address.neighbourhood,
            data.address.city || data.address.town || data.address.county || data.address.state_district,
            data.address.state,
          ].filter(Boolean);
          return parts.join(", ");
        }
        return "Verified GPS Location";
      } catch (e) {
        return "Verified GPS Location";
      }
    };

    const loadAddresses = async () => {
      setLoadingLocations(true);
      setCheckInLocationName("");
      setCheckOutLocationName("");

      const checkInLoc = selectedItem.attendance?.checkIn?.location;
      if (checkInLoc?.latitude && checkInLoc?.longitude) {
        fetchAddress(checkInLoc.latitude, checkInLoc.longitude).then((addr) => {
          setCheckInLocationName(addr);
        });
      }

      const checkOutLoc = selectedItem.attendance?.checkOut?.location;
      if (checkOutLoc?.latitude && checkOutLoc?.longitude) {
        fetchAddress(checkOutLoc.latitude, checkOutLoc.longitude).then((addr) => {
          setCheckOutLocationName(addr);
        });
      }

      setLoadingLocations(false);
    };

    loadAddresses();
  }, [selectedItem]);

  // Assignment Modal & Drag-and-Drop / Search / Pin state
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentData, setAssignmentData] = useState({ assigned: [], unassigned: [] });
  const [loadingAssignment, setLoadingAssignment] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [draggedOver, setDraggedOver] = useState(null); // "myTeam" | "available" | null
  const [draggedEmpId, setDraggedEmpId] = useState(null);

  // Pinned Employees state (persisted in localStorage)
  const [pinnedIds, setPinnedIds] = useState(() => {
    try {
      const saved = localStorage.getItem("superadmin_pinned_employees");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const togglePin = (employeeId, e) => {
    e.stopPropagation();
    setPinnedIds((prev) => {
      const isPinned = prev.includes(employeeId);
      const updated = isPinned
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId];
      try {
        localStorage.setItem("superadmin_pinned_employees", JSON.stringify(updated));
      } catch (err) {}
      return updated;
    });
  };

  const loadData = useCallback(async () => {
    try {
      const res = await getSuperAdminDashboard();
      if (res.success) {
        setDashboardData(res.data || []);
        setTodayDate(res.date || "");
      } else {
        toast.error(res.message || "Failed to load team attendance");
      }
    } catch (err) {
      console.error("Dashboard load error:", err);
      toast.error("Unable to connect to server");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const loadAssignmentData = async () => {
    setLoadingAssignment(true);
    try {
      const res = await getSuperAdminEmployees();
      if (res.success) {
        setAssignmentData(res.data);
      }
    } catch (err) {
      toast.error("Failed to load employee list");
    } finally {
      setLoadingAssignment(false);
    }
  };

  const openAssignmentModal = () => {
    setSearchTerm("");
    setDraggedOver(null);
    setDraggedEmpId(null);
    setAssignmentModalOpen(true);
    loadAssignmentData();
  };

  const handleAssign = async (employeeId) => {
    try {
      const res = await assignEmployeeToSuperAdmin(employeeId);
      if (res.success) {
        toast.success(res.message);
        loadAssignmentData();
        loadData();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error("Failed to assign employee");
    }
  };

  const handleRemove = async (employeeId) => {
    try {
      const res = await removeEmployeeFromSuperAdmin(employeeId);
      if (res.success) {
        toast.success(res.message);
        loadAssignmentData();
        loadData();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error("Failed to remove employee assignment");
    }
  };

  const handleViewHistory = async (employeeId, employeeName) => {
    setHistoryFromDate("");
    setHistoryToDate("");
    setHistoryModal({ employeeName, history: [], loading: true });
    try {
      const res = await getEmployeeAttendanceHistory(employeeId);
      if (res.success) {
        setHistoryModal({
          employeeName: res.employee?.name || employeeName,
          history: res.data || [],
          loading: false,
        });
      } else {
        toast.error(res.message || "Failed to load attendance history");
        setHistoryModal(null);
      }
    } catch (err) {
      toast.error("Forbidden or error fetching history");
      setHistoryModal(null);
    }
  };

  const isDateInRange = (recordDate, fromStr, toStr) => {
    if (!recordDate) return true;
    let recFormatted = "";
    try {
      const s = String(recordDate).trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        recFormatted = s.slice(0, 10);
      } else {
        const d = new Date(s);
        if (!isNaN(d.getTime())) {
          recFormatted = d.toISOString().slice(0, 10);
        }
      }
    } catch (e) {
      return true;
    }
    if (!recFormatted) return true;
    if (fromStr && recFormatted < fromStr) return false;
    if (toStr && recFormatted > toStr) return false;
    return true;
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith("http://") || photoPath.startsWith("https://")) return photoPath;
    const cleanPath = photoPath.startsWith("/") ? photoPath : `/${photoPath}`;
    return `https://dbskillsattendance.iilos.org${cleanPath}`;
  };

  // Calculated summary statistics
  const totalAssigned = dashboardData.length;
  const checkedInCount = dashboardData.filter((item) => item.attendance?.checkIn?.time).length;
  const workingCount = dashboardData.filter(
    (item) => item.attendance?.checkIn?.time && !item.attendance?.checkOut?.time
  ).length;
  const notCheckedInCount = dashboardData.filter(
    (item) => !item.attendance?.checkIn?.time
  ).length;

  // Filtered Main Team List for dashboard search
  const filteredDashboardTeam = dashboardData.filter(
    (item) =>
      item.name?.toLowerCase().includes(mainSearchTerm.toLowerCase()) ||
      item.designation?.toLowerCase().includes(mainSearchTerm.toLowerCase())
  );

  // Filtered & Pinned-first sorted assigned & unassigned lists for modal
  const filteredAssigned = (assignmentData.assigned || [])
    .filter(
      (emp) =>
        emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.designation?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const isAPinned = pinnedIds.includes(a._id);
      const isBPinned = pinnedIds.includes(b._id);
      if (isAPinned && !isBPinned) return -1;
      if (!isAPinned && isBPinned) return 1;
      return 0;
    });

  const filteredUnassigned = (assignmentData.unassigned || [])
    .filter(
      (emp) =>
        emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.designation?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const isAPinned = pinnedIds.includes(a._id);
      const isBPinned = pinnedIds.includes(b._id);
      if (isAPinned && !isBPinned) return -1;
      if (!isAPinned && isBPinned) return 1;
      return 0;
    });

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 p-4 sm:p-6 lg:p-8">
      {/* ── TOP HEADER ── */}
      <div className="max-w-7xl mx-auto mb-6 bg-white rounded-2xl p-6 shadow-xs border border-slate-200/80">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Super Admin Dashboard
            </h1>
            <p className="text-sm font-semibold text-slate-500 mt-1 flex items-center gap-2">
              <Calendar size={15} className="text-indigo-600" />
              Today's Employee Attendance &bull;{" "}
              <span className="text-indigo-600 font-bold">
                {formatDateDisplay(todayDate) || formatDateDisplay(new Date())}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold transition shadow-2xs cursor-pointer"
            >
              <RefreshCw
                size={16}
                className={refreshing ? "animate-spin text-indigo-600" : "text-slate-500"}
              />
              Refresh
            </button>

            <button
              onClick={openAssignmentModal}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition shadow-xs cursor-pointer"
            >
              <UserPlus size={16} /> Manage Employees
            </button>
          </div>
        </div>
      </div>

      {/* ── SUMMARY CARDS ── */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-5 shadow-xs border border-slate-200/80">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
            My Team
          </p>
          <p className="text-3xl font-black text-slate-900">{totalAssigned}</p>
          <p className="text-xs text-slate-500 font-medium mt-1">Assigned Employees</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-xs border border-slate-200/80">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
            Checked In
          </p>
          <p className="text-3xl font-black text-emerald-600">{checkedInCount}</p>
          <p className="text-xs text-slate-500 font-medium mt-1">Today</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-xs border border-slate-200/80">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
            Working
          </p>
          <p className="text-3xl font-black text-amber-600">{workingCount}</p>
          <p className="text-xs text-slate-500 font-medium mt-1">Currently</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-xs border border-slate-200/80">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
            Not Checked In
          </p>
          <p className="text-3xl font-black text-slate-400">{notCheckedInCount}</p>
          <p className="text-xs text-slate-500 font-medium mt-1">Today</p>
        </div>
      </div>

      {/* ── MAIN EMPLOYEE TABLE VIEW ── */}
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
        {/* Table Top Bar */}
        <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/40">
          <div className="flex items-center gap-3">
            <h2 className="font-extrabold text-slate-900 text-sm tracking-wider uppercase">
              MY TEAM
            </h2>
            <span className="bg-indigo-50 text-indigo-700 text-xs font-bold px-2.5 py-0.5 rounded-full border border-indigo-200/60">
              {formatShortDate(todayDate) || formatShortDate(new Date())}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Search Team Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search team..."
                value={mainSearchTerm}
                onChange={(e) => setMainSearchTerm(e.target.value)}
                className="w-48 sm:w-64 pl-8 pr-8 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 transition"
              />
              <Search size={13} className="absolute left-2.5 top-2 text-slate-400" />
              {mainSearchTerm && (
                <button
                  onClick={() => setMainSearchTerm("")}
                  className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <span className="text-xs text-slate-500 font-semibold hidden sm:inline">
              {filteredDashboardTeam.length} / {totalAssigned}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <div className="inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-500 text-sm mt-3">Loading team attendance...</p>
          </div>
        ) : dashboardData.length === 0 ? (
          <div className="py-16 text-center">
            <AlertCircle size={36} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-700 font-bold text-base">No employees assigned yet</p>
            <p className="text-slate-400 text-xs mt-1">
              Click "Manage Employees" to assign team members to your dashboard.
            </p>
          </div>
        ) : filteredDashboardTeam.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-slate-500 text-sm font-semibold">No team members match your search.</p>
          </div>
        ) : (
          <>
            {/* Desktop / Laptop Neat Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-400 uppercase text-[11px] font-extrabold tracking-wider">
                    <th className="py-3.5 px-6">Employee</th>
                    <th className="py-3.5 px-6">Account</th>
                    <th className="py-3.5 px-6">Check In</th>
                    <th className="py-3.5 px-6">Check Out</th>
                    <th className="py-3.5 px-6">Working Hours</th>
                    <th className="py-3.5 px-6">Activity State</th>
                    <th className="py-3.5 px-6 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredDashboardTeam.map((item) => {
                    const activityInfo = getActivityInfo(item.attendance);
                    return (
                      <tr
                        key={item.employeeId}
                        className="hover:bg-slate-50/80 transition cursor-pointer"
                        onClick={() => setSelectedItem(item)}
                      >
                        {/* Employee Details & Avatar */}
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 font-black text-xs flex items-center justify-center flex-shrink-0 border border-indigo-200/60 shadow-2xs">
                              {getInitials(item.name)}
                            </div>
                            <div>
                              <p className="text-slate-900 font-bold leading-snug">
                                {item.name}
                              </p>
                              <p className="text-xs text-slate-500 font-medium">
                                {item.designation || "Employee"}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Account Status Badge */}
                        <td className="py-4 px-6">
                          {item.isActive ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-500 border border-slate-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                              Inactive
                            </span>
                          )}
                        </td>

                        {/* Check In Time */}
                        <td className="py-4 px-6">
                          {item.attendance.checkIn?.time ? (
                            <div className="flex flex-col">
                              <span className="inline-flex items-center gap-1.5 text-slate-900 font-extrabold text-sm">
                                <Clock size={14} className="text-emerald-600" />
                                {formatCleanTime(item.attendance.checkIn.time)}
                              </span>
                              {/* {item.attendance.checkIn.status && (
                                <span className="text-[11px] text-emerald-600 font-semibold pl-5">
                                  {item.attendance.checkIn.status}
                                </span>
                              )} */}
                   
                            </div>
                          ) : (
                            <span className="text-slate-300 font-medium">&mdash;</span>
                          )}
                        </td>

                        {/* Check Out Time */}
                        <td className="py-4 px-6">
                          {item.attendance.checkOut?.time ? (
                            <div className="flex flex-col">
                              <span className="inline-flex items-center gap-1.5 text-slate-900 font-extrabold text-sm">
                                <Clock size={14} className="text-indigo-600" />
                                {formatCleanTime(item.attendance.checkOut.time)}
                              </span>
                              {item.attendance.checkOut.status && (
                                <span className="text-[11px] text-indigo-600 font-semibold pl-5">
                                  {item.attendance.checkOut.status}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300 font-medium">&mdash;</span>
                          )}
                        </td>

                        {/* Working Hours */}
                        <td className="py-4 px-6">
                          {item.attendance.workingHours ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-black bg-slate-100 text-slate-800 border border-slate-200">
                              {item.attendance.workingHours}
                            </span>
                          ) : (
                            <span className="text-slate-300 font-medium">&mdash;</span>
                          )}
                        </td>

                        {/* Activity State Badge */}
                        <td className="py-4 px-6">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold border ${activityInfo.badgeClass}`}
                          >
                            <span className={`w-2 h-2 rounded-full ${activityInfo.dotClass}`}></span>
                            {activityInfo.label}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-4 px-6 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => setSelectedItem(item)}
                              className="inline-flex items-center gap-1.5 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-extrabold px-3 py-1.5 rounded-lg border border-indigo-200/60 shadow-2xs transition cursor-pointer"
                            >
                              <Eye size={13} />
                              View Details
                            </button>
                            <button
                              onClick={() => handleViewHistory(item.employeeId, item.name)}
                              className="inline-flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-3 py-1.5 rounded-lg border border-slate-200/80 shadow-2xs transition cursor-pointer"
                            >
                              <History size={13} />
                              History
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile / Tablet Cards View */}
            <div className="block md:hidden p-4 space-y-3">
              {filteredDashboardTeam.map((item) => {
                const activityInfo = getActivityInfo(item.attendance);
                return (
                  <div
                    key={item.employeeId}
                    className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3"
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 font-black text-xs flex items-center justify-center border border-indigo-200/50">
                          {getInitials(item.name)}
                        </div>
                        <div>
                          <p className="text-slate-900 font-bold text-base">{item.name}</p>
                          <p className="text-xs text-slate-500 font-medium">{item.designation || "N/A"}</p>
                        </div>
                      </div>

                      {item.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span> Inactive
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 p-2.5 rounded-lg text-center">
                      <div>
                        <span className="text-slate-400 font-semibold block mb-0.5">Check In</span>
                        <span className="font-extrabold text-slate-900">
                          {formatCleanTime(item.attendance.checkIn?.time)}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 font-semibold block mb-0.5">Check Out</span>
                        <span className="font-extrabold text-slate-900">
                          {formatCleanTime(item.attendance.checkOut?.time)}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 font-semibold block mb-0.5">Hours</span>
                        <span className="font-black text-slate-900">
                          {item.attendance.workingHours || "--"}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold border ${activityInfo.badgeClass}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${activityInfo.dotClass}`}></span>
                        {activityInfo.label}
                      </span>

                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setSelectedItem(item)}
                          className="inline-flex items-center gap-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-extrabold px-2.5 py-1.5 rounded-lg border border-indigo-200/60 shadow-2xs transition cursor-pointer"
                        >
                          <Eye size={12} />
                          View Details
                        </button>
                        <button
                          onClick={() => handleViewHistory(item.employeeId, item.name)}
                          className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-2.5 py-1.5 rounded-lg border border-slate-200/80 shadow-2xs transition cursor-pointer"
                        >
                          <History size={12} />
                          History
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── EMPLOYEE DETAIL MODAL ── */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200 p-6 relative">
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-5 right-5 text-slate-400 hover:text-slate-700 bg-slate-100 p-1.5 rounded-full transition cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Employee Header */}
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-slate-100">
              <div className="w-14 h-14 rounded-full bg-indigo-100 text-indigo-700 font-black text-lg flex items-center justify-center border border-indigo-200">
                {getInitials(selectedItem.name)}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-black text-slate-900">{selectedItem.name}</h3>
                  {selectedItem.isActive ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-bold bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-bold bg-slate-100 text-slate-500 rounded-full border border-slate-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span> Inactive
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 font-medium">
                  {selectedItem.designation || "No Designation"}
                </p>
              </div>
            </div>

            <p className="text-xs font-extrabold text-slate-400 uppercase tracking-wider mb-4">
              Today's Attendance Details &bull; {formatShortDate(todayDate) || formatShortDate(new Date())}
            </p>

            {/* Check In & Check Out Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Check In Card */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
                    <CheckCircle size={14} /> Check In
                  </span>
                  <span className="text-sm font-black text-slate-900">
                    {formatCleanTime(selectedItem.attendance.checkIn?.time)}
                  </span>
                </div>

                {selectedItem.attendance.checkIn?.photo ? (
                  <div
                    onClick={() => setLightboxPhoto(getPhotoUrl(selectedItem.attendance.checkIn.photo))}
                    className="mb-3 rounded-lg overflow-hidden border border-slate-200 bg-slate-200 aspect-video relative group cursor-pointer"
                  >
                    <img
                      src={getPhotoUrl(selectedItem.attendance.checkIn.photo)}
                      alt="Check-in"
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs font-bold gap-1">
                      <Eye size={16} /> Click to enlarge
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 rounded-lg border border-dashed border-slate-300 py-6 text-center text-slate-400 text-xs">
                    <Camera size={20} className="mx-auto mb-1 opacity-50" /> No Check-in Photo
                  </div>
                )}

                {selectedItem.attendance.checkIn?.location ? (
                  <div className="text-xs text-slate-600 bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
                    <p className="font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5">
                      <MapPin size={14} className="text-emerald-600 flex-shrink-0" />
                      <span>Check-in Location</span>
                    </p>
                    <p className="text-xs text-slate-800 font-medium mb-2.5 leading-relaxed bg-slate-50 p-2.5 rounded-lg border border-slate-100 min-h-[42px] flex items-center">
                      {checkInLocationName || (loadingLocations ? "Fetching location address..." : "Verified GPS Location")}
                    </p>
                    <a
                      href={`https://www.google.com/maps?q=${selectedItem.attendance.checkIn.location.latitude},${selectedItem.attendance.checkIn.location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 font-extrabold hover:underline"
                    >
                      <ExternalLink size={12} /> View on Google Maps
                    </a>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No check-in location</p>
                )}
              </div>

              {/* Check Out Card */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1">
                    <CheckCircle size={14} /> Check Out
                  </span>
                  <span className="text-sm font-black text-slate-900">
                    {formatCleanTime(selectedItem.attendance.checkOut?.time)}
                  </span>
                </div>

                {selectedItem.attendance.checkOut?.photo ? (
                  <div
                    onClick={() => setLightboxPhoto(getPhotoUrl(selectedItem.attendance.checkOut.photo))}
                    className="mb-3 rounded-lg overflow-hidden border border-slate-200 bg-slate-200 aspect-video relative group cursor-pointer"
                  >
                    <img
                      src={getPhotoUrl(selectedItem.attendance.checkOut.photo)}
                      alt="Check-out"
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs font-bold gap-1">
                      <Eye size={16} /> Click to enlarge
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 rounded-lg border border-dashed border-slate-300 py-6 text-center text-slate-400 text-xs">
                    <Camera size={20} className="mx-auto mb-1 opacity-50" /> No Check-out Photo
                  </div>
                )}

                {selectedItem.attendance.checkOut?.location ? (
                  <div className="text-xs text-slate-600 bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
                    <p className="font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5">
                      <MapPin size={14} className="text-indigo-600 flex-shrink-0" />
                      <span>Check-out Location</span>
                    </p>
                    <p className="text-xs text-slate-800 font-medium mb-2.5 leading-relaxed bg-slate-50 p-2.5 rounded-lg border border-slate-100 min-h-[42px] flex items-center">
                      {checkOutLocationName || (loadingLocations ? "Fetching location address..." : "Verified GPS Location")}
                    </p>
                    <a
                      href={`https://www.google.com/maps?q=${selectedItem.attendance.checkOut.location.latitude},${selectedItem.attendance.checkOut.location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 font-extrabold hover:underline"
                    >
                      <ExternalLink size={12} /> View on Google Maps
                    </a>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No check-out location</p>
                )}
              </div>
            </div>

            {/* Working Hours & Activity Summary */}
            <div className="grid grid-cols-2 gap-4 bg-slate-100/70 p-4 rounded-xl mb-6 text-sm">
              <div>
                <span className="text-xs text-slate-500 font-medium block">Working Hours</span>
                <span className="font-black text-slate-900 text-base">
                  {selectedItem.attendance.workingHours || "--"}
                </span>
              </div>
              <div>
                <span className="text-xs text-slate-500 font-medium block">Activity Status</span>
                <span className="font-bold text-indigo-700 text-sm">
                  {getActivityInfo(selectedItem.attendance).label}
                </span>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  const empId = selectedItem.employeeId;
                  const empName = selectedItem.name;
                  setSelectedItem(null);
                  handleViewHistory(empId, empName);
                }}
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-black text-white text-xs font-bold px-4 py-2.5 rounded-xl transition cursor-pointer"
              >
                <History size={14} /> Attendance History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ATTENDANCE HISTORY MODAL ── */}
      {historyModal && (() => {
        const filteredHistory = (historyModal.history || []).filter((rec) =>
          isDateInRange(rec.date, historyFromDate, historyToDate)
        );

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-xl border border-slate-200 p-6 relative">
              <button
                onClick={() => setHistoryModal(null)}
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-700 bg-slate-100 p-1.5 rounded-full transition cursor-pointer"
              >
                <X size={18} />
              </button>

              <h3 className="text-xl font-extrabold text-slate-900 mb-1">Attendance History</h3>
              <p className="text-sm font-semibold text-slate-500 mb-4">{historyModal.employeeName}</p>

              {/* Date Filter Bar */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                  <Calendar size={15} className="text-indigo-600" />
                  <span>Filter Date Range:</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1">
                    <span className="text-[11px] font-semibold text-slate-400">From:</span>
                    <input
                      type="date"
                      value={historyFromDate}
                      onChange={(e) => setHistoryFromDate(e.target.value)}
                      className="text-xs font-bold text-slate-800 bg-transparent outline-none cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1">
                    <span className="text-[11px] font-semibold text-slate-400">To:</span>
                    <input
                      type="date"
                      value={historyToDate}
                      onChange={(e) => setHistoryToDate(e.target.value)}
                      className="text-xs font-bold text-slate-800 bg-transparent outline-none cursor-pointer"
                    />
                  </div>
                  {(historyFromDate || historyToDate) && (
                    <button
                      onClick={() => {
                        setHistoryFromDate("");
                        setHistoryToDate("");
                      }}
                      className="text-xs font-bold text-rose-600 hover:bg-rose-50 px-2 py-1 rounded-lg transition cursor-pointer"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              </div>

              {historyModal.loading ? (
                <div className="py-12 text-center">
                  <div className="inline-block w-6 h-6 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-slate-400 text-xs mt-2">Loading attendance log...</p>
                </div>
              ) : historyModal.history.length === 0 ? (
                <p className="py-12 text-center text-slate-400 text-sm">No past attendance records.</p>
              ) : filteredHistory.length === 0 ? (
                <p className="py-12 text-center text-slate-400 text-sm">
                  No attendance records found for the selected date range.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider">
                        <th className="py-3 px-4">Date</th>
                        <th className="py-3 px-4">Check In</th>
                        <th className="py-3 px-4">Check Out</th>
                        <th className="py-3 px-4">Hours</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium">
                      {filteredHistory.map((record) => (
                        <tr key={record._id} className="hover:bg-slate-50/80 transition">
                          <td className="py-3.5 px-4 font-bold text-slate-900 whitespace-nowrap">
                            {formatShortDate(record.date)}
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2.5">
                              {record.checkIn?.photo ? (
                                <div
                                  onClick={() => setLightboxPhoto(getPhotoUrl(record.checkIn.photo))}
                                  className="w-10 h-10 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 relative group cursor-pointer flex-shrink-0 shadow-2xs"
                                  title="Click to view check-in photo"
                                >
                                  <img
                                    src={getPhotoUrl(record.checkIn.photo)}
                                    alt="Check In"
                                    className="w-full h-full object-cover group-hover:scale-110 transition duration-200"
                                  />
                                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                                    <Eye size={12} />
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="w-10 h-10 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300 flex-shrink-0"
                                  title="No photo"
                                >
                                  <Camera size={14} />
                                </div>
                              )}
                              <div>
                                <p className="text-emerald-700 font-bold">{formatCleanTime(record.checkIn?.time)}</p>
                                {record.checkIn?.location && (
                                  <a
                                    href={`https://www.google.com/maps?q=${record.checkIn.location.latitude},${record.checkIn.location.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-indigo-600 font-semibold"
                                  >
                                    <MapPin size={10} /> Map
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2.5">
                              {record.checkOut?.photo ? (
                                <div
                                  onClick={() => setLightboxPhoto(getPhotoUrl(record.checkOut.photo))}
                                  className="w-10 h-10 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 relative group cursor-pointer flex-shrink-0 shadow-2xs"
                                  title="Click to view check-out photo"
                                >
                                  <img
                                    src={getPhotoUrl(record.checkOut.photo)}
                                    alt="Check Out"
                                    className="w-full h-full object-cover group-hover:scale-110 transition duration-200"
                                  />
                                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                                    <Eye size={12} />
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="w-10 h-10 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300 flex-shrink-0"
                                  title="No photo"
                                >
                                  <Camera size={14} />
                                </div>
                              )}
                              <div>
                                <p className="text-indigo-700 font-bold">{formatCleanTime(record.checkOut?.time)}</p>
                                {record.checkOut?.location && (
                                  <a
                                    href={`https://www.google.com/maps?q=${record.checkOut.location.latitude},${record.checkOut.location.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-indigo-600 font-semibold"
                                  >
                                    <MapPin size={10} /> Map
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3.5 px-4 text-slate-700 font-bold">
                            {record.workingHours || "--"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })()}


      {/* ── MANAGE EMPLOYEES (ASSIGNMENT MODAL WITH SEARCH, PIN, & DRAG-AND-DROP) ── */}
      {assignmentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-xl border border-slate-200 p-6 relative">
            <button
              onClick={() => setAssignmentModalOpen(false)}
              className="absolute top-5 right-5 text-slate-400 hover:text-slate-700 bg-slate-100 p-1.5 rounded-full transition cursor-pointer"
            >
              <X size={18} />
            </button>

            <h3 className="text-xl font-extrabold text-slate-900 mb-1">Manage My Team</h3>
            <p className="text-xs text-slate-500 mb-4">
              Assign or remove team members. Pin employees to keep them at the top. Drag & drop cards between columns or click Assign/Remove.
            </p>

            {/* Search Input Bar */}
            <div className="relative mb-5">
              <input
                type="text"
                placeholder="Search team or available employees by name or designation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-9 py-2.5 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white transition"
              />
              <Search size={15} className="absolute left-3 top-3 text-slate-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {loadingAssignment ? (
              <div className="py-12 text-center">
                <div className="inline-block w-6 h-6 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* MY TEAM (Assigned Drop Zone) */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (draggedOver !== "myTeam") setDraggedOver("myTeam");
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setDraggedOver(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDraggedOver(null);
                    const empId = e.dataTransfer.getData("text/plain") || draggedEmpId;
                    if (empId) {
                      handleAssign(empId);
                      setDraggedEmpId(null);
                    }
                  }}
                  className={`rounded-xl p-4 transition-all ${
                    draggedOver === "myTeam"
                      ? "bg-indigo-50/70 border-2 border-dashed border-indigo-500 shadow-md scale-[1.01]"
                      : "bg-slate-50 border border-slate-200"
                  }`}
                >
                  <h4 className="font-extrabold text-slate-900 text-xs uppercase tracking-wider mb-3 flex items-center justify-between">
                    <span>MY TEAM ({filteredAssigned.length})</span>
                    {draggedOver === "myTeam" && (
                      <span className="text-[11px] text-indigo-600 font-bold animate-pulse">
                        Drop to Assign
                      </span>
                    )}
                  </h4>

                  {filteredAssigned.length === 0 ? (
                    <p className="text-xs text-slate-400 italic py-8 text-center border-2 border-dashed border-slate-200/60 rounded-lg">
                      {searchTerm
                        ? "No matching employees found in My Team"
                        : "Drag & drop employees here to assign"}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {filteredAssigned.map((emp) => {
                        const isPinned = pinnedIds.includes(emp._id);
                        return (
                          <div
                            key={emp._id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("text/plain", emp._id);
                              e.dataTransfer.effectAllowed = "move";
                              setDraggedEmpId(emp._id);
                            }}
                            onDragEnd={() => {
                              setDraggedEmpId(null);
                              setDraggedOver(null);
                            }}
                            className={`bg-white p-3 rounded-lg border shadow-2xs flex items-center justify-between group hover:border-indigo-300 cursor-grab active:cursor-grabbing transition ${
                              isPinned ? "border-amber-300 bg-amber-50/20" : "border-slate-200"
                            } ${draggedEmpId === emp._id ? "opacity-40 scale-95" : ""}`}
                          >
                            <div className="flex items-center gap-2.5">
                              <GripVertical size={14} className="text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                              <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {getInitials(emp.name)}
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="text-xs font-bold text-slate-900">{emp.name}</p>
                                  {isPinned && (
                                    <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.2 rounded">
                                      Pinned
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-500">{emp.designation || "Employee"}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={(e) => togglePin(emp._id, e)}
                                className={`p-1.5 rounded-lg transition cursor-pointer ${
                                  isPinned
                                    ? "text-amber-600 bg-amber-100 hover:bg-amber-200"
                                    : "text-slate-300 hover:text-amber-500 hover:bg-slate-100 opacity-0 group-hover:opacity-100"
                                }`}
                                title={isPinned ? "Unpin employee" : "Pin employee to top"}
                              >
                                <Pin size={13} className={isPinned ? "fill-amber-600" : ""} />
                              </button>
                              <button
                                onClick={() => handleRemove(emp._id)}
                                className="text-xs text-rose-600 hover:bg-rose-50 px-2 py-1 rounded-lg font-bold transition cursor-pointer"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* AVAILABLE EMPLOYEES (Unassigned Drop Zone with Pin Support) */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (draggedOver !== "available") setDraggedOver("available");
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setDraggedOver(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDraggedOver(null);
                    const empId = e.dataTransfer.getData("text/plain") || draggedEmpId;
                    if (empId) {
                      handleRemove(empId);
                      setDraggedEmpId(null);
                    }
                  }}
                  className={`rounded-xl p-4 transition-all ${
                    draggedOver === "available"
                      ? "bg-rose-50/70 border-2 border-dashed border-rose-400 shadow-md scale-[1.01]"
                      : "bg-slate-50 border border-slate-200"
                  }`}
                >
                  <h4 className="font-extrabold text-slate-900 text-xs uppercase tracking-wider mb-3 flex items-center justify-between">
                    <span>AVAILABLE EMPLOYEES ({filteredUnassigned.length})</span>
                    {draggedOver === "available" && (
                      <span className="text-[11px] text-rose-600 font-bold animate-pulse">
                        Drop to Remove
                      </span>
                    )}
                  </h4>

                  {filteredUnassigned.length === 0 ? (
                    <p className="text-xs text-slate-400 italic py-8 text-center border-2 border-dashed border-slate-200/60 rounded-lg">
                      {searchTerm
                        ? "No matching available employees found"
                        : "All employees assigned"}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {filteredUnassigned.map((emp) => {
                        const isPinned = pinnedIds.includes(emp._id);
                        return (
                          <div
                            key={emp._id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("text/plain", emp._id);
                              e.dataTransfer.effectAllowed = "move";
                              setDraggedEmpId(emp._id);
                            }}
                            onDragEnd={() => {
                              setDraggedEmpId(null);
                              setDraggedOver(null);
                            }}
                            className={`bg-white p-3 rounded-lg border shadow-2xs flex items-center justify-between group hover:border-indigo-300 cursor-grab active:cursor-grabbing transition ${
                              isPinned ? "border-amber-300 bg-amber-50/30" : "border-slate-200"
                            } ${draggedEmpId === emp._id ? "opacity-40 scale-95" : ""}`}
                          >
                            <div className="flex items-center gap-2.5">
                              <GripVertical size={14} className="text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                              <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {getInitials(emp.name)}
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="text-xs font-bold text-slate-900">{emp.name}</p>
                                  {isPinned && (
                                    <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.2 rounded">
                                      Pinned
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-500">{emp.designation || "Employee"}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={(e) => togglePin(emp._id, e)}
                                className={`p-1.5 rounded-lg transition cursor-pointer ${
                                  isPinned
                                    ? "text-amber-600 bg-amber-100 hover:bg-amber-200"
                                    : "text-slate-300 hover:text-amber-500 hover:bg-slate-100 opacity-0 group-hover:opacity-100"
                                }`}
                                title={isPinned ? "Unpin employee" : "Pin employee to top"}
                              >
                                <Pin size={13} className={isPinned ? "fill-amber-600" : ""} />
                              </button>
                              <button
                                onClick={() => handleAssign(emp._id)}
                                className="text-xs text-indigo-600 hover:bg-indigo-50 px-2 py-1 rounded-lg font-bold transition cursor-pointer"
                              >
                                Assign
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── IMAGE LIGHTBOX VIEWER (Z-[100] TO OVERLAY OVER ALL MODALS) ── */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 backdrop-blur-xs"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-6 right-6 text-white hover:text-slate-300 bg-white/10 p-2 rounded-full cursor-pointer transition z-[101]"
          >
            <X size={24} />
          </button>
          <div className="max-w-4xl w-full max-h-[90vh] flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxPhoto}
              alt="Enlarged View"
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl border border-white/10"
            />
          </div>
        </div>
      )}
      

    </div>
  );
};

export default SuperAdminDashboard;
